import { useState } from "react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowLeft, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong");
      }
      setSent(true);
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Error",
        description: (err as Error)?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md text-center">
          <div className="flex justify-center mb-6">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-14 h-14 object-contain" />
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Check your inbox</h1>
            <p className="text-slate-500 text-sm leading-relaxed mb-2">
              We've sent a password reset link to:
            </p>
            <p className="font-semibold text-slate-800 text-sm mb-6 bg-slate-50 rounded-lg px-4 py-2.5 inline-block">
              {email}
            </p>
            <p className="text-slate-400 text-xs leading-relaxed mb-8">
              The link expires in 1 hour. Check your spam folder if you don't see it within a few minutes.
            </p>
            <Button
              variant="outline"
              className="w-full h-11 rounded-xl font-medium"
              onClick={() => { setSent(false); setEmail(""); }}
            >
              Try a different email
            </Button>
          </div>
          <div className="mt-5 text-center">
            <Link href="/app/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel */}
      <div
        className="hidden md:flex flex-col justify-center px-12 relative overflow-hidden"
        style={{
          flex: "0 0 45%",
          background: "linear-gradient(160deg, #1e3a5f 0%, #2563eb 50%, #1d4ed8 100%)",
        }}
      >
        <div className="relative z-10 max-w-sm mx-auto">
          <div className="mb-8">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-24 h-24 object-contain" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">FinProject</h2>
          <p className="text-lg font-medium mb-10 text-blue-200">Construction Finance Platform</p>
          <div className="p-6 rounded-2xl" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
              <Mail className="w-5 h-5 text-blue-200" />
            </div>
            <p className="text-sm text-blue-100 leading-relaxed">
              Enter the email address linked to your account and we'll send you a secure reset link within seconds.
            </p>
          </div>
          <div className="mt-14 pt-8 border-t border-blue-700/40">
            <p className="text-xs text-blue-300/60">Trusted by construction finance teams nationwide</p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8 md:hidden">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-16 h-16 object-contain mb-3" />
            <span className="text-xl font-bold text-slate-900 mb-1">FinProject</span>
            <p className="text-sm text-slate-500">Construction Finance Platform</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <div className="mb-7">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Forgot your password?</h1>
              <p className="text-sm text-slate-500">No worries — we'll email you a reset link right away.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold tracking-wide uppercase text-slate-500">Email Address</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:ring-blue-400"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-xl font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white border-0"
              >
                {loading
                  ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sending link...</span>
                  : "Send Reset Link"
                }
              </Button>
            </form>
          </div>

          <div className="mt-5 text-center">
            <Link href="/app/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
